import axios from 'axios'
import { format, isBefore } from 'date-fns'
import Dialog from 'lib/components/Dialog/Dialog'
import AuthContext from 'lib/context/AuthContext'
import useFocusOnMount from 'lib/hooks/useFocusOnMount'
import useOnInputChange from 'lib/hooks/useOnInputChange'
import { reportError } from 'lib/util/errorHandler'
import ActionDialog from 'pages/Home/admin/ActionDialog/ActionDialog'
import FormGroup from 'pages/Home/admin/FormGroup/FormGroup'
import RecordedWebinarType from 'pages/Home/RecordedWebinarType'
import { UPCOMING_WEBINAR_DURATION_MIN } from 'pages/Home/UpcomingWebinarsSection/util'
import UpcomingWebinarType from 'pages/Home/UpcomingWebinarType'
import WebinarSelector from 'pages/WebinarDetail/admin/WebinarSelector/WebinarSelector'
import React, { FormEvent, useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export interface AddUpcomingWebinarDialogProps {
  initialUpcomingWebinar?: UpcomingWebinarType
  recordedWebinars?: RecordedWebinarType[]
  onRequestClose: ()=> void
}

type EditableUpcomingWebinarType = UpcomingWebinarType & { date: string }

function buildInitialState(initialUpcomingWebinar?: UpcomingWebinarType) {
  if (initialUpcomingWebinar) {
    return {
      ...initialUpcomingWebinar,
      date: initialUpcomingWebinar.date ? format(new Date(initialUpcomingWebinar.date), 'yyyy-MM-dd\'T\'hh:mm') : '',
      registrationLink: initialUpcomingWebinar.registrationLink || ''
    }
  }

  return {
    recordedWebinarId: '',
    title: '',
    presenter: '',
    registrationLink: '',
    description: '',
    date: '',
    durationMin: UPCOMING_WEBINAR_DURATION_MIN,
    published: false
  }
}

export default function AddUpcomingWebinarDialog(props: AddUpcomingWebinarDialogProps) {
  const { initialUpcomingWebinar, onRequestClose, recordedWebinars } = props
  const [t] = useTranslation()
  const [apiState, setApiState] = useState({ error: null, saving: false })
  const { getAccessToken } = useContext(AuthContext)
  const [state, setState] = useState(buildInitialState(initialUpcomingWebinar))
  const onInputChange = useOnInputChange<EditableUpcomingWebinarType>(setState)
  const inputEl = useRef(null)
  useFocusOnMount(inputEl)
  const { recordedWebinarId, title, presenter, registrationLink, description, date, durationMin, published } = state
  const isEditing = initialUpcomingWebinar && initialUpcomingWebinar.id

  const validationError = (() => {
    if (
      !title ||
      !recordedWebinarId ||
      !presenter ||
      !registrationLink ||
      !description.trim() ||
      !date ||
      !durationMin
    ) {
      return t('home.upcomingWebinarsSection.admin.addDialog.validationError')
    }
    if (isBefore(new Date(date), new Date())) {
      return t('home.upcomingWebinarsSection.admin.addDialog.pastDateError')
    }
  })()

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (validationError) {
      return
    }
    setApiState({ saving: true, error: null })
    try {
      const accessToken = await getAccessToken()
      const headers = { Authorization: `Bearer ${accessToken}` }
      const upcomingWebinar = { ...state, date: new Date(date).valueOf() }
      if (isEditing) {
        await axios.put(
          `${process.env.THREESIXTY_TRAINING_URI}/admin/upcoming-webinars/${initialUpcomingWebinar.id}`,
          upcomingWebinar,
          { headers }
        )
      } else {
        await axios.post(`${process.env.THREESIXTY_TRAINING_URI}/admin/upcoming-webinars`, upcomingWebinar, { headers })
      }
      window.location.reload()
    } catch (err) {
      setApiState({ saving: false, error: t('home.upcomingWebinarsSection.admin.addDialog.apiError') })
      reportError(err, `Error ${initialUpcomingWebinar ? 'editing' : 'adding'} upcoming webinar`, {
        upcomingWebinarId: initialUpcomingWebinar ? initialUpcomingWebinar.id : null
      })
    }
  }

  function renderSeries() {
    const selectedRecordedWebinar = recordedWebinars.find(recordedWebinar => recordedWebinarId === recordedWebinar.id)

    return (
      selectedRecordedWebinar && (
        <div style={{ fontStyle: 'italic', paddingBottom: '10px' }}>{selectedRecordedWebinar.series.title}</div>
      )
    )
  }

  const { error, saving } = apiState
  const errorMessage = error || validationError

  return (
    <Dialog onRequestClose={onRequestClose}>
      <Dialog.Header onRequestClose={onRequestClose}>
        <Dialog.Title>
          {isEditing
            ? t('home.upcomingWebinarsSection.admin.addDialog.editTitle')
            : t('home.upcomingWebinarsSection.admin.addDialog.title')}
        </Dialog.Title>
      </Dialog.Header>
      <form onSubmit={onSubmit}>
        <ActionDialog.Body>
          <WebinarSelector webinars={recordedWebinars} selectedId={recordedWebinarId} onChange={onInputChange} />
          {renderSeries()}
          {state.recordedWebinarId && (
            <>
              <FormGroup.Input
                onChange={onInputChange}
                name="title"
                placeholder={t('home.upcomingWebinarsSection.admin.addDialog.titlePlaceholder')}
                value={title}
                notBlank
              />
              <FormGroup.Input
                onChange={onInputChange}
                name="presenter"
                placeholder={t('home.upcomingWebinarsSection.admin.addDialog.presenterPlaceholder')}
                value={presenter}
                notBlank
              />
              <FormGroup.Input
                type="url"
                pattern="https://.*"
                onChange={onInputChange}
                name="registrationLink"
                placeholder={t('home.upcomingWebinarsSection.admin.addDialog.registrationPlaceholder')}
                value={registrationLink}
                required
              />
              <FormGroup.Textarea
                onChange={onInputChange}
                name="description"
                placeholder={t('home.upcomingWebinarsSection.admin.addDialog.descriptionPlaceholder')}
                rows={5}
                value={description}
                required
              />
              <FormGroup.Input
                label={t('home.upcomingWebinarsSection.admin.addDialog.datePlaceholder')}
                onChange={onInputChange}
                name="date"
                placeholder={t('home.upcomingWebinarsSection.admin.addDialog.datePlaceholder')}
                type="datetime-local"
                value={date}
                notBlank
              />
              <h4>{t('home.upcomingWebinarsSection.admin.addDialog.makePublic')}</h4>
              <FormGroup.Input
                label={t('home.upcomingWebinarsSection.admin.addDialog.publish')}
                onChange={onInputChange}
                name="published"
                placeholder={t('home.upcomingWebinarsSection.admin.addDialog.publish')}
                type="checkbox"
                checked={published}
              />
            </>
          )}
        </ActionDialog.Body>
        <ActionDialog.Footer>
          <div>
            <ActionDialog.CancelButton onClick={onRequestClose} />
            <ActionDialog.SaveButton loading={saving} />
          </div>
          <ActionDialog.Error message={errorMessage} />
        </ActionDialog.Footer>
      </form>
    </Dialog>
  )
}
